import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import unitConversionService, { UnitConversion } from '../../services/unitConversionService';
import { parseApiError } from '../../utils/errorUtils';
import '../Products/ProductCategoryDeletePage.css';
import { ArrowLeft, Trash2 } from 'lucide-react';

const UnitConversionDeletePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [conversion, setConversion] = useState<UnitConversion | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    loadConversion();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadConversion = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await unitConversionService.getUnitConversionById(parseInt(id || '0', 10));
      if (data) {
        setConversion(data);
      }
      setLoading(false);
    } catch (err: unknown) {
      setError(parseApiError(err, t('unitConversion.failedToLoadConversion')).message);
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!conversion) {
      setError(t('unitConversion.failedToLoadConversion'));
      return;
    }

    setIsDeleting(true);
    setError(null);
    try {
      await unitConversionService.deleteUnitConversion(conversion.unitConversionId);

      navigate('/products/unit-conversions', {
        state: { message: t('unitConversion.deletedSuccess') },
      });
    } catch (err: unknown) {
      setError(parseApiError(err, t('unitConversion.failedToDeleteConversion')).message);
      setIsDeleting(false);
    }
  };

  const handleCancel = () => {
    navigate(`/products/unit-conversions/${conversion?.unitConversionId}`);
  };

  if (loading) {
    return (
      <Layout>
        <div className="delete-loading-container">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (!conversion) {
    return (
      <Layout>
        <div className="delete-container">
          <div className="alert alert-danger">{error || t('unitConversion.conversionNotFound')}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="delete-container">
        <nav className="delete-breadcrumb">
          <button
            className="delete-breadcrumb-btn"
            onClick={handleCancel}
            title={t('unitConversion.title')}
          >
            <ArrowLeft size={16} strokeWidth={2} />
            {t('unitConversion.title')}
          </button>
        </nav>

        <div className="delete-content">
          <div className="delete-card delete-card-main">
            <div className="delete-card-header delete-card-header-warning">
              <Trash2 size={24} strokeWidth={2} className="delete-icon" />
              <h2 className="delete-card-title">{t('common.delete')}</h2>
            </div>

            {error && (
              <div className="alert alert-danger alert-dismissible fade show">
                <strong>{t('common.error')}:</strong> {error}
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setError(null)}
                  aria-label={t('common.close')}
                ></button>
              </div>
            )}

            <div className="delete-card-body">
              <div className="delete-warning-section">
                <p className="delete-warning-text">
                  {t('unitConversion.deleteConfirmationMessage')}
                </p>
              </div>

              <div className="delete-category-info">
                <div className="delete-info-item">
                  <label className="delete-info-label">{t('unitConversion.fromUnit')}</label>
                  <p className="delete-info-value">{conversion.fromUnitName}</p>
                </div>

                <div className="delete-info-item">
                  <label className="delete-info-label">{t('unitConversion.toUnit')}</label>
                  <p className="delete-info-value">{conversion.toUnitName}</p>
                </div>

                <div className="delete-info-item">
                  <label className="delete-info-label">{t('unitConversion.conversionFactor')}</label>
                  <p className="delete-info-value delete-code">{conversion.conversionFactor}</p>
                </div>

                {conversion.remarks && (
                  <div className="delete-info-item delete-info-item-full">
                    <label className="delete-info-label">{t('unitConversion.remarks')}</label>
                    <p className="delete-info-value">{conversion.remarks}</p>
                  </div>
                )}
              </div>

              <div className="delete-actions">
                <button
                  className="btn-cancel"
                  onClick={handleCancel}
                  disabled={isDeleting}
                  title={t('common.cancel')}
                >
                  {t('common.cancel')}
                </button>

                <button
                  className="btn-delete"
                  onClick={handleDelete}
                  disabled={isDeleting}
                  title={t('common.delete')}
                >
                  {isDeleting ? (
                    <>
                      <span className="btn-spinner"></span>
                      {t('common.deleting')}
                    </>
                  ) : (
                    <>
                      <Trash2 size={16} strokeWidth={2} />
                      {t('common.delete')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="delete-sidebar">
            <div className="delete-card">
              <div className="delete-card-header">
                <h3 className="delete-card-title">{t('common.information')}</h3>
              </div>
              <div className="delete-card-body">
                <div className="delete-help-section">
                  <div className="delete-help-item">
                    <strong className="delete-help-strong">{t('common.warning')}</strong>
                    <p>{t('unitConversion.deleteConfirmationMessage')}</p>
                  </div>
                  <div className="delete-help-item">
                    <strong className="delete-help-strong">{t('unitConversion.permanentAction')}</strong>
                    <p>{t('unitConversion.deleteWarning')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default UnitConversionDeletePage;
